import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Mail,
  Search,
  ShieldCheck,
  Users,
  UserPlus,
} from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Avatar, gradientFor, initialsFor } from "../../components/Avatar/Avatar";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { Card } from "../../components/Card/Card";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { Input } from "../../components/Input/Input";
import { Menu } from "../../components/Menu/Menu";
import { Modal } from "../../components/Modal/Modal";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Table, Column } from "../../components/Table/Table";
import { useToast } from "../../components/Toast/Toast";

import { useAuth } from "../auth/useAuth";
import { ApiError, describeError } from "../../lib/errors";
import { getProject } from "../projects/api";

import {
  getTeam,
  getProjectPendingInvites,
  removeMember,
  leaveProject,
  revokeInvite,
  resendInvite,
  TeamMember,
} from "./api";
import type { AdminInvite } from "../invites/api";
import { InviteMemberModal } from "./InviteMemberModal";
import styles from "./TeamPage.module.css";

type Confirm =
  | { kind: "remove"; member: TeamMember }
  | { kind: "leave"; member: TeamMember }
  | null;

// === Formatting helpers ============================================== //

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** "MMM d, yyyy" (e.g. "Apr 22, 2024"). */
function formatJoined(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return DATE_FMT.format(d);
}

/** Compact relative time: "just now", "2h ago", "1d ago", or "Apr 22". */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return DATE_FMT.format(d);
}

/**
 * Humanise a duration until a future ISO timestamp.
 * "today", "in 70h", "in 3d", "in 2 weeks" etc — used for invite expiry meta.
 * Returns { text, isClose } where isClose is true when < 24h to expiry.
 */
export function humaniseExpiresIn(iso: string): {
  text: string;
  isClose: boolean;
  expired: boolean;
} {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return { text: "—", isClose: false, expired: false };
  }
  const diffMs = d.getTime() - Date.now();
  if (diffMs <= 0) return { text: "expired", isClose: true, expired: true };
  const min = Math.floor(diffMs / 60000);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  const isClose = hr < 24;
  let text: string;
  if (min < 60) text = `expires in ${min}m`;
  else if (hr < 48) text = `expires in ${hr}h`;
  else if (day < 14) text = `expires in ${day}d`;
  else text = `expires in ${Math.floor(day / 7)}w`;
  return { text, isClose, expired: false };
}

function formatInviteSent(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const day = Math.floor(diffMs / 86_400_000);
  if (day <= 0) return "today";
  if (day === 1) return "yesterday";
  if (day < 7) return `${day} days ago`;
  return DATE_FMT.format(d);
}

// === Component ====================================================== //

export function TeamPage() {
  const { id = "" } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { push } = useToast();
  const { user } = useAuth();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [revokeTarget, setRevokeTarget] = useState<AdminInvite | null>(null);
  const [query, setQuery] = useState("");

  const isSuperAdmin = user?.role === "super_admin";

  const { data, isLoading } = useQuery({
    queryKey: ["team", id],
    queryFn: () => getTeam(id),
    enabled: !!id,
  });

  // Project name is used in the breadcrumb + modal titles.
  const { data: project } = useQuery({
    queryKey: ["project", id],
    queryFn: () => getProject(id).then((r) => r.data),
    enabled: !!id,
  });

  // Pending invites scoped to this project.
  const { data: pendingInvites = [] } = useQuery({
    queryKey: ["pending-invites", id],
    queryFn: () => getProjectPendingInvites(id),
    enabled: !!id,
  });

  const removeMut = useMutation({
    mutationFn: ({ userId }: { userId: string }) => removeMember(id, userId),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["team", id] });
      qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
      const name = data?.members.find((m) => m.user_id === vars.userId)?.name;
      push({
        variant: "success",
        message: name ? `${name} removed from project.` : "Member removed.",
      });
      setConfirm(null);
    },
    onError: (err) => {
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
  });

  const leaveMut = useMutation({
    mutationFn: ({ userId }: { userId: string }) => leaveProject(id, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team", id] });
      qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
      push({ variant: "success", message: "You left the project." });
      setConfirm(null);
    },
    onError: (err) => {
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
  });

  const revokeMut = useMutation({
    mutationFn: (inviteId: string) => revokeInvite(inviteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pending-invites", id] });
      qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
      push({ variant: "success", message: "Invite revoked." });
      setRevokeTarget(null);
    },
    onError: (err) => {
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
  });

  const resendMut = useMutation({
    mutationFn: ({ inviteId }: { inviteId: string; email: string }) =>
      resendInvite(inviteId),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["pending-invites", id] });
      qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
      push({
        variant: "success",
        message: `Invite re-sent to ${vars.email}.`,
      });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "invite_not_pending") {
        push({
          variant: "warn",
          message: "This invite can't be resent — send a new one.",
        });
        return;
      }
      if (err instanceof ApiError && err.code === "not_found") {
        push({
          variant: "warn",
          message: "This invite no longer exists — send a new one.",
        });
        return;
      }
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
  });

  const filteredMembers = useMemo(() => {
    const members = data?.members ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q),
    );
  }, [data, query]);

  const breadcrumb = [
    { label: "All projects", to: "/projects" },
    { label: project?.name ?? "Project" },
    { label: "Team" },
  ];

  const header = (
    <PageHeader
      breadcrumb={breadcrumb}
      title="Team"
      actions={
        <Button onClick={() => setInviteOpen(true)}>
          <UserPlus size={14} aria-hidden="true" />
          <span style={{ marginLeft: 6 }}>Invite member</span>
        </Button>
      }
    />
  );

  if (isLoading) {
    return (
      <>
        {header}
        <div className={styles.page}>Loading…</div>
      </>
    );
  }
  if (!data) return header;

  function copyEmail(email: string) {
    try {
      navigator.clipboard?.writeText(email);
      push({ variant: "success", message: "Email copied." });
    } catch {
      push({ variant: "danger", message: "Failed to copy." });
    }
  }

  // === Member table columns =========================================== //
  const columns: Column<TeamMember>[] = [
    {
      key: "name",
      header: "Member",
      width: "35%",
      cell: (m) => {
        const isSelf = m.user_id === user?.id;
        return (
          <div className={styles.memberCell}>
            <Avatar name={m.name} size={32} />
            <div className={styles.memberInfo}>
              <span className={styles.memberName}>
                {m.name}
                {isSelf && <span className={styles.youMark}>· you</span>}
              </span>
              <span className={styles.memberEmail}>{m.email}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: "role",
      header: "Role",
      cell: () => <Badge variant="user">Member</Badge>,
    },
    {
      key: "joined",
      header: "Joined",
      cell: (m) => (
        <span className={styles.muted}>{formatJoined(m.joined_at)}</span>
      ),
    },
    {
      key: "last_login",
      header: "Last login",
      cell: (m) => (
        <span className={styles.muted}>{formatRelative(m.last_login_at)}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "60px",
      cell: (m) => {
        const isSelf = m.user_id === user?.id;
        const items = [
          {
            label: "Copy email",
            icon: <Mail size={13} aria-hidden="true" />,
            onSelect: () => copyEmail(m.email),
          },
          { separator: true as const },
          isSelf
            ? {
                label: "Leave project",
                variant: "danger" as const,
                onSelect: () => setConfirm({ kind: "leave", member: m }),
              }
            : {
                label: "Remove from project",
                variant: "danger" as const,
                onSelect: () => setConfirm({ kind: "remove", member: m }),
              },
        ];
        return (
          <div className={styles.actionsCell}>
            <Menu items={items} aria-label={`Actions for ${m.name}`} />
          </div>
        );
      },
    },
  ];

  const memberCount = data.members.length;

  // === Render ========================================================= //
  return (
    <>
      {header}
      <div className={styles.page}>
        {/* Super admin access callout — mockup L362-L368 */}
        {data.super_admins_count > 0 && (
          <div className={styles.superCallout} role="note">
            <span className={styles.superCalloutIcon} aria-hidden="true">
              <ShieldCheck size={18} />
            </span>
            <span className={styles.superCalloutTitle}>
              Super admin access · {data.super_admins_count}{" "}
              {data.super_admins_count === 1 ? "user" : "users"}
            </span>
            <span className={styles.superCalloutBody}>
              Super admins have access to all projects platform-wide.
              They&apos;re not part of this team.
            </span>
            {isSuperAdmin && (
              <Link
                to="/users?role=super_admin"
                className={styles.superCalloutLink}
              >
                View →
              </Link>
            )}
          </div>
        )}

        {/* Toolbar — search + count chip (mockup L371-L378) */}
        {memberCount > 0 && (
          <div className={styles.toolbar}>
            <div className={styles.search}>
              <Input
                aria-label="Search team members"
                placeholder="Search by name or email…"
                value={query}
                leadingIcon={<Search size={14} aria-hidden="true" />}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className={styles.toolbarSpacer} />
            <Badge variant="neutral">
              {memberCount} {memberCount === 1 ? "member" : "members"}
            </Badge>
          </div>
        )}

        {/* Members table */}
        {memberCount === 0 ? (
          <Card>
            <EmptyState
              icon={<Users aria-hidden="true" />}
              title="No team members yet"
              description="Invite your first teammate."
              action={
                <Button onClick={() => setInviteOpen(true)}>
                  <UserPlus size={14} aria-hidden="true" />
                  <span style={{ marginLeft: 6 }}>Invite member</span>
                </Button>
              }
            />
          </Card>
        ) : filteredMembers.length === 0 ? (
          <Card>
            <div className={styles.noResults}>
              No matches for &laquo;{query}&raquo;
            </div>
          </Card>
        ) : (
          <Table columns={columns} rows={filteredMembers} />
        )}

        {/* Pending invites (mockup L459-L493) — hidden when empty */}
        {pendingInvites.length > 0 && (
          <Card>
            <div className={styles.invitesCardHeader}>
              <h3 className={styles.invitesCardTitle}>Pending invites</h3>
              <Badge variant="warning">
                {pendingInvites.length} outstanding · sent via email
              </Badge>
            </div>
            <div className={styles.invitesCardBody}>
              {pendingInvites.map((inv) => {
                const exp = humaniseExpiresIn(inv.expires_at);
                const sentLabel = formatInviteSent(inv.created_at);
                return (
                  <div key={inv.id} className={styles.inviteRow}>
                    <span className={styles.inviteIcon} aria-hidden="true">
                      <Mail size={18} />
                    </span>
                    <div className={styles.inviteInfo}>
                      <div className={styles.inviteEmail}>{inv.email}</div>
                      <div className={styles.inviteMeta}>
                        Invited by{" "}
                        <span className={styles.inviteMetaInviter}>
                          {/* TODO: backend exposes invited_by as a user id —
                              email lookup not available in this v1. Show id
                              for super_admin troubleshooting. */}
                          {inv.invited_by}
                        </span>{" "}
                        · {sentLabel} ·{" "}
                        <span
                          className={
                            exp.isClose ? styles.inviteMetaWarn : undefined
                          }
                        >
                          {exp.text}
                        </span>
                      </div>
                    </div>
                    <div className={styles.inviteActions}>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={resendMut.isPending}
                        onClick={() =>
                          resendMut.mutate({
                            inviteId: inv.id,
                            email: inv.email,
                          })
                        }
                      >
                        Resend
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRevokeTarget(inv)}
                        style={{ color: "var(--danger)" }}
                      >
                        Revoke
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Invite modal */}
        <InviteMemberModal
          projectId={id}
          projectName={project?.name}
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
        />

        {/* Remove / Leave confirmation */}
        {confirm && (
          <RemoveConfirmModal
            kind={confirm.kind}
            member={confirm.member}
            projectName={project?.name ?? "this project"}
            pending={removeMut.isPending || leaveMut.isPending}
            onCancel={() => setConfirm(null)}
            onConfirm={() => {
              if (confirm.kind === "remove") {
                removeMut.mutate({ userId: confirm.member.user_id });
              } else {
                leaveMut.mutate({ userId: confirm.member.user_id });
              }
            }}
          />
        )}

        {/* Revoke invite confirmation */}
        {revokeTarget && (
          <Modal
            open
            onClose={() => setRevokeTarget(null)}
            title="Revoke invite?"
            footer={
              <>
                <Button
                  variant="secondary"
                  onClick={() => setRevokeTarget(null)}
                  disabled={revokeMut.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={() => revokeMut.mutate(revokeTarget.id)}
                  disabled={revokeMut.isPending}
                >
                  Revoke invite
                </Button>
              </>
            }
          >
            <p>
              The invite link sent to <strong>{revokeTarget.email}</strong>{" "}
              will stop working immediately.
            </p>
          </Modal>
        )}
      </div>
    </>
  );
}

// === Remove / Leave confirmation modal ============================== //

interface RemoveConfirmProps {
  kind: "remove" | "leave";
  member: TeamMember;
  projectName: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function RemoveConfirmModal({
  kind,
  member,
  projectName,
  pending,
  onCancel,
  onConfirm,
}: RemoveConfirmProps) {
  const title =
    kind === "remove"
      ? `Remove ${member.name} from project?`
      : `Leave ${projectName}?`;
  const cta = kind === "remove" ? "Remove from project" : "Leave project";
  const dangerCopy =
    kind === "remove" ? (
      <>
        <strong>
          Their active session for this project will be revoked immediately.
        </strong>{" "}
        The account itself stays — they may still have access to other
        projects.
      </>
    ) : (
      <>
        You&apos;ll lose access to this project immediately. To rejoin, ask a
        project admin to invite you.
      </>
    );
  return (
    <Modal
      open
      onClose={onCancel}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={pending}>
            {cta}
          </Button>
        </>
      }
    >
      <div className={styles.memberPreview}>
        <div
          aria-hidden="true"
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: gradientFor(member.name),
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontFamily: "var(--font-display)",
            fontSize: 13,
            flexShrink: 0,
          }}
        >
          {initialsFor(member.name)}
        </div>
        <div className={styles.memberPreviewInfo}>
          <div className={styles.memberPreviewName}>{member.name}</div>
          <div className={styles.memberPreviewMeta}>
            {member.email} · Member · joined {formatJoined(member.joined_at)}
          </div>
        </div>
      </div>
      <Alert variant="danger">{dangerCopy}</Alert>
    </Modal>
  );
}
